import { RouteElement } from '@components'
import { Container } from '@components-deprecated'

import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router'

import { CommonFormDataContextProvider } from '../../contexts/commonFormDataContext'
import { useInboxOrganizationId } from '../../contexts/inboxOrganizationIdContext'
import { withRouteLinkClickedListener } from '../../hocs/withRouteLinkClickedListener'
import { AccountsContextProvider } from '../app/accounts'
import { contactsRequested } from '../app/contacts/action-creators'
import { UserOrganizationContextProvider } from '../app/organization'
import { accountsRequest, taxRatesRequest } from './action-creators'
import { BillCreate, JournalCreate, PartnerInbox, VoucherView } from './pages'
import { InboxRoutes } from './types'

type InboxProps = {
  basePath: string
  customerView?: boolean
  umbrellaId?: string
  userOrganizationId?: string
}

// Exported unwrapped in addition to the default `Inbox` below: Umbrella.tsx renders this nested
// inside its own tree, which already carries a withRouteLinkClickedListener at the Umbrella root.
// Since both share the same hash history, wrapping this too would mount a second
// useCustomEventsRoutes() listening for the same RoutingLinkClicked event, so a single link click
// would call navigate() twice - the observed cause of Settings needing a duplicate Back press when
// entered from the umbrella Inbox. The standalone `inbox-module` web component (web-components/
// components/Inbox.tsx) has no such ancestor, so it still needs the wrapped export.
export const InboxContent = ({ basePath, customerView, umbrellaId, userOrganizationId }: InboxProps) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useInboxOrganizationId()
  const dispatch = useDispatch()

  useEffect(() => {
    if (customerView && selectedOrganizationId !== userOrganizationId) {
      setSelectedOrganizationId(userOrganizationId)
    }
  }, [customerView, selectedOrganizationId, setSelectedOrganizationId, userOrganizationId])

  useEffect(() => {
    if (!selectedOrganizationId) {
      return
    }

    dispatch(taxRatesRequest(selectedOrganizationId))
    dispatch(accountsRequest(selectedOrganizationId))
    dispatch(
      contactsRequested({
        organizationId: selectedOrganizationId,
        page: { offset: 0 },
        filters: { isVendor: true, text: null },
      }),
    )
  }, [dispatch, selectedOrganizationId])

  return (
    <UserOrganizationContextProvider organizationId={selectedOrganizationId}>
      <AccountsContextProvider organizationId={selectedOrganizationId}>
        <CommonFormDataContextProvider>
          <Container>
            {/* Relative paths: Inbox is mounted either at the router root ("/", own web component)
                  or nested under Umbrella's own real v6 match on basePath (which strips that prefix
                  before this <Routes> ever sees the pathname) - relative paths resolve correctly in
                  both cases without needing to know which absolute prefix was actually consumed. */}
            <Routes>
              <Route
                index
                element={
                  <RouteElement isHashRouter>
                    <PartnerInbox basePath={basePath} customerView={customerView} umbrellaId={umbrellaId} />
                  </RouteElement>
                }
              />
              <Route
                path={`:voucherId/${InboxRoutes.ViewVoucher}`}
                element={
                  <RouteElement isHashRouter>
                    {selectedOrganizationId && <VoucherView customerView={customerView} />}
                  </RouteElement>
                }
              />
              <Route
                path={`:voucherId/${InboxRoutes.CreateBill}`}
                element={
                  <RouteElement isHashRouter>
                    {customerView ? <Navigate to="/" replace /> : <BillCreate customerView={customerView} />}
                  </RouteElement>
                }
              />
              <Route
                path={`:voucherId/${InboxRoutes.CreateJournal}`}
                element={
                  <RouteElement isHashRouter>
                    {customerView ? <Navigate to="/" replace /> : selectedOrganizationId && <JournalCreate />}
                  </RouteElement>
                }
              />
            </Routes>
          </Container>
        </CommonFormDataContextProvider>
      </AccountsContextProvider>
    </UserOrganizationContextProvider>
  )
}

export const Inbox = withRouteLinkClickedListener(InboxContent)
